<template>
  <div v-if="loading">
    <vx-card>
      <h3>Loading... Please wait...</h3>
    </vx-card>
  </div>
  <div v-else>
    <vs-row>
      <vs-col vs-xs="12" vs-sm="4" vs-lg="8" class="px-0 pr-1">
        <vs-input
          class="w-full"
          label="Contract Number"
          v-model="contract.contract_number"
          :warning="api_errors.contract_number"
          :warning-text="
            api_errors.contract_number ? api_errors.contract_number[0] : null
          "
          :disabled="view_only"
        />
      </vs-col>
      <vs-col vs-xs="12" vs-sm="4" vs-lg="2" class="px-1">
        <SelectDate
          label="Trade Date"
          :selectedDate.sync="contract.trade_date"
          :warning="api_errors.trade_date"
          :warning_text="
            api_errors.trade_date ? api_errors.trade_date[0] : null
          "
          :disabled="view_only"
          @change="load_live_fifo_data()"
        />
      </vs-col>
      <vs-col vs-xs="12" vs-sm="4" vs-lg="2" class="px-0 pl-1 pt-4">
        <vs-button
          class="mt-1 w-full"
          color="primary"
          type="filled"
          @click.prevent="save"
          :disabled="submit_disabled || !is_contract_valid"
          v-if="!view_only"
          >Save</vs-button
        >
      </vs-col>
    </vs-row>
    <vs-row v-if="!contract.portfolio">
      <vs-col vs-xs="12" vs-sm="12" vs-lg="12" class="px-0 pr-1">
        <label class="vs-input--label">Entity</label>
        <SelectForeign
          :model.sync="selected_entity"
          placeholder="Type to Search"
          container_class="w-auto"
          :multiple="false"
          :load_initial="true"
          :label_keys="['name']"
          label_class="mb-0"
          input_class="mt-0 bg-white"
          :show_id_in_label="true"
          :endpoint="$store.state.apiendpoints.entity_list"
          :use_id_as_value="true"
          :disabled="view_only"
          list_length="10000"
        />
      </vs-col>
    </vs-row>
    <vs-row>
      <vs-col vs-xs="12" vs-sm="3" vs-lg="3" class="px-0 pr-1">
        <vs-input
          :value="contract.sequence_number"
          label="Sequence Number"
          placeholder="Will be auto generated on Save"
          class="w-full"
          :readonly="true"
          :disabled="view_only"
        />
      </vs-col>
      <vs-col vs-xs="12" vs-sm="3" vs-lg="3" class="px-0 pr-1">
        <label class="vs-input--label">Portfolio</label>
        <SelectForeign
          :model.sync="contract.portfolio"
          placeholder="Type to Search"
          container_class="w-auto"
          :multiple="false"
          :load_initial="true"
          :label_keys="['name']"
          label_class="mb-0"
          input_class="mt-0 bg-white"
          :show_id_in_label="true"
          :endpoint="`${$store.state.apiendpoints.portfolio_list}${selected_entity?'?entity='+selected_entity:''}`"
          :warning="api_errors.portfolio"
          :warning_text="
            api_errors.portfolio ? api_errors.portfolio.join(',') : null
          "
          :use_id_as_value="false"
          :disabled="view_only || (!selected_entity && !contract.portfolio)"
          list_length="10000"
          @change="load_live_fifo_data()"
        />
      </vs-col>
      <vs-col vs-xs="12" vs-sm="3" vs-lg="3" class="px-0 pr-1">
        <label class="vs-input--label">Broker</label>
        <SelectForeign
          :model.sync="contract.broker"
          placeholder="Type to Search"
          container_class="w-auto"
          :multiple="false"
          :load_initial="true"
          :label_keys="['name']"
          label_class="mb-0"
          input_class="mt-0 bg-white"
          :show_id_in_label="true"
          :endpoint="$store.state.apiendpoints.broker_list"
          :warning="api_errors.broker"
          :warning_text="api_errors.broker ? api_errors.broker.join(',') : null"
          :use_id_as_value="false"
          :disabled="view_only"
          list_length="10000"
        />
      </vs-col>
      <vs-col vs-xs="12" vs-sm="3" vs-lg="3" class="px-0 pr-1">
        <label class="vs-input--label">Demat Account</label>
        <SelectForeign
          :model.sync="contract.demat_account"
          placeholder="Type to Search"
          container_class="w-auto"
          :multiple="false"
          :load_initial="true"
          :label_keys="['name']"
          label_class="mb-0"
          input_class="mt-0 bg-white"
          :show_id_in_label="true"
          :endpoint="$store.state.apiendpoints.demat_account_list"
          :warning="api_errors.demat_account"
          :warning_text="
            api_errors.demat_account ? api_errors.demat_account.join(',') : null
          "
          :use_id_as_value="false"
          :disabled="view_only"
          list_length="10000"
        />
      </vs-col>
      <vs-col vs-xs="12" vs-sm="6" vs-lg="6" class="px-0 pr-1">
        <vs-input
          v-model="contract.narration"
          label="Narration"
          placeholder="Enter Narration Here"
          class="w-full"
          :disabled="view_only"
        />
      </vs-col>
      <vs-col vs-xs="12" vs-sm="3" vs-lg="3" class="px-0 pr-1">
        <vs-input
          v-model="contract.other_charges"
          label="Other Charges"
          class="w-full"
          type="Number"
          step="0.001"
          :disabled="view_only"
        />
      </vs-col>
      <vs-col vs-xs="12" vs-sm="3" vs-lg="3" class="px-0 pr-1">
        <vs-input
          v-model="contract.total_contract_value"
          label="Total Contract Value"
          class="w-full"
          type="Number"
          step="0.001"
          :disabled="view_only"
        />
      </vs-col>
    </vs-row>
    <div class="mt-base" style="display: flex; justify-content: end;">
      <vs-button size="small" color="danger" @click="left_quantity={}">Clear FIFO Data</vs-button>
    </div>
    <vs-row class="mt-2">
      <vs-col vs-xs="12" class="px-0">
        <vs-table
          :data="contract.items"
          noDataText="No Items Added"
          id="items-table"
        >
          <template slot="thead">
            <vs-th class="input-custom-dropdown--header">Contract Type</vs-th>
            <vs-th class="input-custom-dropdown--header">Share</vs-th>
            <vs-th class="input-custom-dropdown--header">Exchange</vs-th>
            <vs-th style="min-width: 200px">Quantity</vs-th>
            <vs-th>Market Rate</vs-th>
            <vs-th>STT</vs-th>
            <vs-th>GST</vs-th>
            <vs-th></vs-th>
          </template>
          <vs-tr>
            <vs-td colspan="18">
              <vs-button
                color="primary"
                size="small"
                class="w-full"
                @click="addItem"
                :disabled="view_only"
                v-if="!view_only"
              >
                Add Row
              </vs-button>
            </vs-td>
          </vs-tr>
          <vs-tr v-for="(item, index) in contract.items" :key="index">
            <!-- Contract Type -->
            <vs-td>
              <InputSelect
                :model.sync="item.contract_type"
                placeholder="Select from List"
                container_class="w-auto input-custom-dropdown"
                :multiple="false"
                :warning="item.api_errors && item.api_errors.contract_type"
                :warning_text="
                  item.api_errors && item.api_errors.contract_type
                    ? item.api_errors.contract_type.join(',')
                    : null
                "
                :disabled="view_only"
                :options="contract_type_options"
                @change="check_left_quantity(index)"
              />
            </vs-td>
            <!-- Share -->
            <vs-td>
              <SelectForeign
                :model.sync="item.share"
                placeholder="Type to Search"
                container_class="w-auto input-custom-dropdown"
                :multiple="false"
                :load_initial="true"
                :label_keys="['name']"
                label_class="mb-0"
                input_class="mt-0"
                :show_id_in_label="true"
                :endpoint="$store.state.apiendpoints.share_list"
                :warning="item.api_errors && item.api_errors.share"
                :warning_text="
                  item.api_errors && item.api_errors.share
                    ? item.api_errors.share.join(',')
                    : null
                "
                :disabled="view_only"
                :use_id_as_value="false"
                list_length="10000"
                @change="load_live_fifo_data()"
              />
            </vs-td>
            <!-- Exchange -->
            <vs-td>
              <InputSelect
                :model.sync="item.exchange"
                placeholder="Select from List"
                container_class="w-auto input-custom-dropdown"
                :multiple="false"
                :warning="item.api_errors && item.api_errors.exchange"
                :warning_text="
                  item.api_errors && item.api_errors.exchange
                    ? item.api_errors.exchange.join(',')
                    : null
                "
                :disabled="view_only"
                :options="exchange_options"
              />
            </vs-td>
            <!-- Quantity -->
            <vs-td>
              <vs-input
                class="input-custom-text"
                type="Number"
                step="1"
                min="1"
                v-model="item.quantity"
                :danger="!is_edit_mode && item.api_errors && item.api_errors.quantity"
                :danger-text="
                    !is_edit_mode && item.api_errors && item.api_errors.quantity
                    ? item.api_errors.quantity.join(',')
                    : null
                "
                :warning="is_edit_mode && item.api_errors && item.api_errors.quantity"
                :warning-text="
                    is_edit_mode && item.api_errors && item.api_errors.quantity
                    ? item.api_errors.quantity.join(',')
                    : null
                "
                :disabled="view_only"
                @change="check_left_quantity(index)"
              />
            </vs-td>
            <!-- Market Rate -->
            <vs-td>
              <vs-input
                class="input-custom-text"
                type="Number"
                step="0.000001"
                min="0"
                v-model="item.market_rate"
                :danger="item.api_errors && item.api_errors.market_rate"
                :danger-text="
                  item.api_errors && item.api_errors.market_rate
                    ? item.api_errors.market_rate.join(',')
                    : null
                "
                :disabled="view_only"
              />
            </vs-td>
            <!-- STT -->
            <vs-td>
              <vs-input
                class="input-custom-text"
                type="Number"
                step="0.000001"
                min="0"
                v-model="item.stt"
                :danger="item.api_errors && item.api_errors.stt"
                :danger-text="
                  item.api_errors && item.api_errors.stt
                    ? item.api_errors.stt.join(',')
                    : null
                "
                :disabled="view_only"
              />
            </vs-td>
            <!-- GST -->
            <vs-td>
              <vs-input
                class="input-custom-text"
                type="Number"
                step="0.000001"
                min="0"
                v-model="item.gst"
                :danger="item.api_errors && item.api_errors.gst"
                :danger-text="
                  item.api_errors && item.api_errors.gst
                    ? item.api_errors.gst.join(',')
                    : null
                "
                :disabled="view_only"
              />
            </vs-td>
            <vs-td>
              <vs-button
                color="danger"
                size="small"
                @click="contract.items.splice(index, 1)"
              >
                X
              </vs-button>
            </vs-td>
          </vs-tr>
        </vs-table>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>
import md5 from 'md5'
import { Request } from "../../utils/requests";

export default {
  data() {
    return {
      loading: true,
      view_only: false,
      selected_entity: null,
      contract_type_options: [
        { value: "fp", label: "Fresh Purchase", calc_sign: 1 },
        { value: "ip", label: "IPO", calc_sign: 1 },
        { value: "ri", label: "Right Issue", calc_sign: 1 },
        { value: "ns", label: "Normal Sale", calc_sign: -1 },
        { value: "bb", label: "Buy Back", calc_sign: -1 },
      ],
      exchange_options: [
        { value: "n", label: "NSE" },
        { value: "b", label: "BSE" },
      ],

      contract: {
        contract_number: null,
        trade_date: new Date().toISOString().split("T")[0],
        items: [],

        portfolio: null,
        broker: null,
        demat_account: null,

        narration: null,
        other_charges: 0,
        total_contract_value: 0,
        sequence_number: null,
      },
      contract_id: this.$route.params.contract_id,
      submit_disabled: false,
      api_errors: {},

      left_quantity: {},
      is_valid: {}
    };
  },
  mounted() {
    this.init();
    if (this.$route.query.view) {
      this.submit_disabled = true;
      this.view_only = true;
    }
  },
  watch: {
    "$route.params.contract_id"(newV) {
      this.init();
    },
    total_contract_value(newV) {
      this.contract.total_contract_value = newV;
    },
  },
  computed: {
    total_contract_value() {
      let total = 0;
      total += parseFloat(this.contract.other_charges || 0);
      this.contract.items.forEach((value) => {
        var calc_sign = 1;
        this.contract_type_options.forEach((option) => {
          if (option.value == value.contract_type) {
            calc_sign = option.calc_sign;
          }
        });
        let quantity = parseFloat(value.quantity || 0);
        let rate = parseFloat(value.market_rate || 0);
        let stt = parseFloat(value.stt || 0);
        let gst = parseFloat(value.gst || 0);

        total += calc_sign * (quantity * rate + stt + gst);
      });
      return parseFloat(total).toFixed(4);
    },
    is_contract_valid() {
      if (this.is_edit_mode) return true
      return !(Object.values(this.is_valid).includes(false))
    },
    is_edit_mode() {
      if (this.contract_id && !this.view_only) return true
      return false
    }
  },
  methods: {
    init() {
      this.contract_id = this.$route.params.contract_id;
      if (this.contract_id) {
        this.loadData();
      } else {
        this.loading = false;
        this.contract = {
          contract_number: null,
          trade_date: new Date().toISOString().split("T")[0],
          items: [],

          portfolio: null,
          broker: null,
          demat_account: null,

          narration: null,
          other_charges: 0,
          total_contract_value: 0,
          sequence_number: null,
        };
        this.addItem();
      }
    },
    addItem() {
      this.contract.items.push({
        contract_type: null,

        share: null,
        exchange: null,

        quantity: null,
        market_rate: null,
        stt: null,
        gst: null,
      });
    },
    loadData() {
      this.loading = true;
      this.$vs.loading();

      let url =
        this.$store.state.apiendpoints.contract + this.contract_id + "/";

      Request(this, "GET", url).then(
        (res) => {
          this.contract = res;
          this.load_live_fifo_data()
          this.loading = false;
          this.$vs.loading.close();
        },
        (err) => {
          this.$vs.notify({
            time: 2500,
            title: "Unknown Error",
            text: "An Error Occured while loading data. Please Try Again",
            iconPack: "feather",
            icon: "icon-alert-circle",
            color: "danger",
          });
          this.loading = false;
          this.$vs.loading.close();
          this.$router.push("/");
        }
      );
    },
    save() {
      let $this = this;
      let url = $this.$store.state.apiendpoints.contract,
        request_method = "POST";

      if (this.contract_id) {
        url += this.contract_id + "/";
        request_method = "PUT";
      }

      let data = { ...this.contract };

      this.submit_disabled = true;

      Request($this, request_method, url, null, data).then(
        (res) => {
          $this.$vs.notify({
            time: 2500,
            title: "Saved",
            text: "Saved Successfully",
            iconPack: "feather",
            icon: "icon-check",
            color: "success",
          });
          this.submit_disabled = false;
          $this.$router.push({ name: "contract-list" });
        },
        (err) => {
          if (err.error) {
            this.$vs.notify({
              time: 2500,
              title: "Error",
              text: "Please check all fields and Try Again.",
              iconPack: "feather",
              icon: "icon-alert-circle",
              color: "danger",
            });
            this.api_errors = err.error;
            if (this.api_errors.items) {
              this.contract.items.forEach((v, i) => {
                v.api_errors = this.api_errors.items[i] || null;
              });
            }
          } else {
            $this.$vs.notify({
              time: 2500,
              title: "Unknown Error",
              text: "An Error Occured. Please Try Again",
              iconPack: "feather",
              icon: "icon-alert-circle",
              color: "danger",
            });
          }
          this.submit_disabled = false;
        }
      );
    },
    check_api_errors_for_contract_item(share_id) {

      if (!share_id) return

      // reset errors for share_id
      this.contract.items.forEach((item, i) => {
        if(item.share && item.share.id == share_id) {
          this.$set(item, 'api_errors', null)
        }
      })

      //reset is valid to true for share_id
      this.$set(this.is_valid, share_id, true)

      // if hask key not in left quantity return
      let hash_key = this.get_left_quantity_hash_key(share_id)
      if(!Object.keys(this.left_quantity).includes(hash_key)) return

      // similar share qty summation
      let purchase_quantity = 0
      let summation_qty = this.left_quantity[hash_key]
      this.contract.items.forEach((item, i) => {
        if(item.share && item.share.id == share_id) {
          if (['ns', 'bb'].includes(item.contract_type)) {
            summation_qty= parseInt(summation_qty - parseInt(item.quantity || 0))
          } else {
            summation_qty= parseInt(summation_qty + parseInt(item.quantity || 0))
            purchase_quantity = parseInt(purchase_quantity + parseInt(item.quantity || 0))
          }
        }
      })

      // if summation qty is negative, then set error.
      if (summation_qty < 0) {
        //setting is valid to false for share
        this.$set(this.is_valid, share_id, false)
        // setting error for share
        this.contract.items.forEach((item, i) => {
          if(item.share && item.quantity!=null && ['ns', 'bb'].includes(item.contract_type) && item.share.id == share_id) {
            this.$set(item, 'api_errors', {
              'quantity': [`Consolidated sum of sale is greater than available stock (${this.left_quantity[hash_key]}${purchase_quantity ? ` + ${purchase_quantity}` : ''}). `]
            })
          }
        })
      }
    },
    check_left_quantity(index) {
      // if current index item is not having share selected return 
      if (!(this.contract.items[index].share && this.contract.items[index].share.id)) return

      this.check_api_errors_for_contract_item(this.contract.items[index].share.id)
    },
    load_live_fifo_data() {
      if (!(this.contract.portfolio && this.contract.portfolio.id) || !this.contract.trade_date) return

      for(let i=0; i<this.contract.items.length; i++) {
          if(!this.contract.items[i].share) continue
          this.load_single_live_fifo_data(this.contract.items[i])
      }
    },
    load_single_live_fifo_data(item) {
      if (
        !(this.contract.portfolio && this.contract.portfolio.id) || 
        !this.contract.trade_date || 
        !(item.share && item.share.id) ||
        Object.keys(this.left_quantity).includes(this.get_left_quantity_hash_key(item.share.id))
      ) return

      // setting inital hash key for item share
      let hash_key = this.get_left_quantity_hash_key(item.share.id)
      this.left_quantity[hash_key] = null

      let url = this.$store.state.apiendpoints.live_fifo_table_new
      let payload = {
        portfolio: this.contract.portfolio.id,
        scrip: item.share.id,
        trade_date: this.contract.trade_date
      }

      Request(this, "GET", url, payload, null).then(
        (res) => {
          this.prepare_left_quantity(res, item.share.id)
          this.check_api_errors_for_contract_item(item.share.id)
          this.$vs.notify({
            time: 5000,
            title: `Fifo Data share[${item.share.name}]`,
            text: "Loaded Successfully",
            iconPack: "feather",
            icon: "icon-check",
            color: "success",
          });
        },
        (err) => {
          this.$vs.notify({
            time: 2500,
            title: "Unknown Error",
            text: "An Error Occured while loading live fifo data. Please Try Again",
            iconPack: "feather",
            icon: "icon-alert-circle",
            color: "danger",
          });
        }
      );
    },
    get_left_quantity_hash_key(share_id) {
      return md5(`${this.contract.portfolio.id}_${this.contract.trade_date}_${share_id}`)
    },
    prepare_left_quantity(res, share_id) {
      let key = this.get_left_quantity_hash_key(share_id)
      let left_quantity = 0
      res.forEach(obj => {
        left_quantity+=obj.left
      })
      this.left_quantity[key] = left_quantity
    }
  },
};
</script>

<style lang="scss">
.input-custom-dropdown {
  &--header {
    min-width: 180px;
  }
  .vs__dropdown-toggle {
    border: 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.5);
    border-radius: 0;
  }
  .vs--single .vs__selected {
    white-space: nowrap;
  }
  .vs__selected-options {
    flex-wrap: nowrap;
  }
  .vs__search,
  .vs__search:focus {
    padding: 0;
  }
}
.input-custom-text {
  &.vs-con-input-label {
    width: auto;
    min-width: 70px;
  }
  .vs-input--input {
    border: 0 !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.5) !important;
    border-radius: 0 !important;

    &:focus {
      border: 0 !important;
      border-bottom: 1px solid rgba(0, 0, 0, 0.5) !important;
      box-shadow: none;
    }
  }
}
#items-table {
  /* width */
  ::-webkit-scrollbar {
    width: 3px;
    height: 7px;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 5px;
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 5px;
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: rgb(39, 39, 39);
  }
}

// #items-table .vs-con-tbody {
//     padding-bottom: 150px;
// }

#items-table {
  .vs-table--tbody,
  tr,
  input {
    background: transparent;
  }
  .vs-table--tbody-table tr:last-child td {
    padding-bottom: 150px;
  }
  .vs-con-tbody {
    overflow: hidden;
  }
  .vs-con-tbody:hover,
  .vs-con-tbody:focus {
    overflow: auto;
  }
}

.vs-table--tbody-table .tr-values:hover {
  transform: none !important;
  -webkit-transform: none !important;
}
</style>